import { Box } from '@mui/material';
import { ICellRendererParams } from 'ag-grid-community';
import HistoryIcon from '@mui/icons-material/History';
import { OfferedReportSummaryRow } from '@callmart/types';

interface SumWithHistoryCellRendererProps extends ICellRendererParams {
    data: OfferedReportSummaryRow;
    onHistoryClick?: (rowData: OfferedReportSummaryRow, anchorEl: HTMLElement) => void;
}

export const SumWithHistoryCellRenderer = (params: SumWithHistoryCellRendererProps) => {
    if (!params.data) return null;

    const { sum, hasHistory } = params.data;

    if (sum === null || sum === undefined) {
        return null;
    }

    const handleCellClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (hasHistory && params.onHistoryClick) {
            e.stopPropagation();
            // Capture rect immediately before any state updates
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            // Create virtual element with stable getBoundingClientRect for MUI Popover
            const virtualEl = {
                getBoundingClientRect: () => rect,
                nodeType: 1,
            } as unknown as HTMLElement;
            params.onHistoryClick(params.data, virtualEl);
        }
    };

    const formattedSum = params.valueFormatted;

    return (
        <Box
            onClick={handleCellClick}
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                paddingLeft: '8px',
                cursor: hasHistory ? 'pointer' : 'default',
                '&:hover': hasHistory
                    ? {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      }
                    : {},
            }}
            title={hasHistory ? 'Click to view edit history' : undefined}
        >
            {/* Sum value */}
            <Box component="span" sx={{ marginRight: hasHistory ? '4px' : 0 }}>
                {formattedSum}
            </Box>

            {/* History icon (after number) */}
            {hasHistory && (
                <HistoryIcon
                    sx={{
                        fontSize: 16,
                        color: 'action.active',
                        opacity: 0.7,
                    }}
                />
            )}

            {/* Triangle indicator (top-left corner) */}
            {hasHistory && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: '-3px',
                        width: 0,
                        height: 0,
                        borderStyle: 'solid',
                        borderWidth: '12px 12px 0 0',
                        borderColor: '#FFC107 transparent transparent transparent',
                        pointerEvents: 'none', // Let clicks pass through to parent
                    }}
                />
            )}
        </Box>
    );
};
